import React from 'react';
import { STAGE, stageAlpha } from '../../design/lab/palette';
import {
    isOpenUIComponent,
    isOpenUIUnresolved,
    type OpenUIComponentNode,
    type OpenUIValue,
} from '../../lib/openui';

export interface OpenUIRendererProps {
    node: OpenUIValue;
    onAction?: (label: string, payload?: string) => void;
    compact?: boolean;
}

const MONO: React.CSSProperties = {
    fontFamily: 'var(--bs-mono)',
};

/**
 * Shared renderer for OpenUI Lang 1.0 AST trees.
 * Used by both the `/labs/openui-streaming` workbench and the resident Eos
 * terminal assistant (`ChatReply` / `/genui`).
 */
export const OpenUIRenderer: React.FC<OpenUIRendererProps> = ({ node, onAction, compact = false }) => {
    if (node === null || node === undefined || typeof node === 'boolean') return null;
    if (typeof node === 'string' || typeof node === 'number') {
        return <span style={{ color: STAGE.ink, fontSize: compact ? 12 : 13 }}>{String(node)}</span>;
    }
    if (Array.isArray(node)) {
        return (
            <>
                {node.map((child, idx) => (
                    <OpenUIRenderer key={idx} node={child} onAction={onAction} compact={compact} />
                ))}
            </>
        );
    }
    if (isOpenUIUnresolved(node)) {
        return (
            <div
                data-testid={`unresolved-${node.ref}`}
                style={{
                    ...MONO,
                    padding: compact ? '8px 10px' : '12px 14px',
                    border: `1px dashed ${stageAlpha(STAGE.cyan, 0.4)}`,
                    background: stageAlpha(STAGE.cyan, 0.04),
                    color: STAGE.cyan,
                    fontSize: 11,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                }}
            >
                <span>{`awaiting statement: ${node.ref}`}</span>
                <span style={{ color: STAGE.muted }}>streaming…</span>
            </div>
        );
    }
    if (isOpenUIComponent(node)) {
        return <RenderComponent comp={node} onAction={onAction} compact={compact} />;
    }
    return null;
};

function RenderComponent({
    comp,
    onAction,
    compact,
}: {
    comp: OpenUIComponentNode;
    onAction?: (label: string, payload?: string) => void;
    compact: boolean;
}) {
    const { type, args } = comp;

    switch (type) {
        case 'Card': {
            const children = args[0];
            return (
                <div
                    style={{
                        border: `1px solid ${STAGE.rule}`,
                        background: STAGE.raised,
                        padding: compact ? 14 : 18,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: compact ? 12 : 16,
                    }}
                >
                    <OpenUIRenderer node={children} onAction={onAction} compact={compact} />
                </div>
            );
        }

        case 'Header': {
            const title = typeof args[0] === 'string' ? args[0] : 'Untitled';
            const subtitle = typeof args[1] === 'string' ? args[1] : null;
            const badge = typeof args[2] === 'string' ? args[2] : null;
            return (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                        paddingBottom: 10,
                        borderBottom: `1px solid ${STAGE.rule}`,
                    }}
                >
                    <div>
                        <div style={{ fontFamily: 'var(--bs-serif)', fontStyle: 'italic', fontSize: compact ? 20 : 24, color: STAGE.ink, lineHeight: 1.15 }}>
                            {title}
                        </div>
                        {subtitle && (
                            <div style={{ ...MONO, fontSize: 11, color: STAGE.muted, marginTop: 4 }}>
                                {subtitle}
                            </div>
                        )}
                    </div>
                    {badge && (
                        <span
                            style={{
                                ...MONO,
                                fontSize: 11,
                                padding: '3px 8px',
                                border: `1px solid ${stageAlpha(STAGE.green, 0.45)}`,
                                background: stageAlpha(STAGE.green, 0.1),
                                color: STAGE.green,
                            }}
                        >
                            {badge}
                        </span>
                    )}
                </div>
            );
        }

        case 'Stack': {
            const items = Array.isArray(args[0]) ? args[0] : args;
            return (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(auto-fit, minmax(${compact ? '120px' : '145px'}, 1fr))`,
                        gap: compact ? 8 : 12,
                    }}
                >
                    {items.map((item, idx) => (
                        <OpenUIRenderer key={idx} node={item} onAction={onAction} compact={compact} />
                    ))}
                </div>
            );
        }

        case 'Metric': {
            const label = typeof args[0] === 'string' ? args[0] : 'Metric';
            const value = args[1] !== undefined && args[1] !== null ? String(args[1]) : '—';
            const delta = typeof args[2] === 'string' ? args[2] : null;
            const positive = delta ? delta.startsWith('+') || delta.includes('pass') || delta.includes('-') : true;
            return (
                <div
                    style={{
                        padding: compact ? '10px 12px' : '12px 14px',
                        border: `1px solid ${STAGE.rule}`,
                        background: 'rgba(255,255,255,0.02)',
                    }}
                >
                    <div style={{ ...MONO, fontSize: 11, color: STAGE.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {label}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginTop: 6 }}>
                        <span style={{ ...MONO, fontSize: compact ? 16 : 20, fontWeight: 600, color: STAGE.ink, fontVariantNumeric: 'tabular-nums' }}>
                            {value}
                        </span>
                        {delta && (
                            <span style={{ ...MONO, fontSize: 11, color: positive ? STAGE.green : STAGE.orange }}>
                                {delta}
                            </span>
                        )}
                    </div>
                </div>
            );
        }

        case 'BarChart': {
            const labels = Array.isArray(args[0]) ? args[0].map(String) : [];
            const values = Array.isArray(args[1]) ? args[1].map(v => (typeof v === 'number' ? v : Number(v) || 0)) : [];
            const unit = typeof args[2] === 'string' ? args[2] : '';
            const maxVal = Math.max(...values, 1);
            return (
                <div
                    style={{
                        padding: compact ? 12 : 14,
                        border: `1px solid ${STAGE.rule}`,
                        background: 'rgba(255,255,255,0.015)',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {labels.map((label, i) => {
                            const v = values[i] ?? 0;
                            const pct = Math.max(0.04, Math.min(1, v / maxVal));
                            return (
                                <div key={label + i} style={{ display: 'grid', gridTemplateColumns: '96px minmax(0, 1fr) 56px', alignItems: 'center', gap: 10 }}>
                                    <span style={{ ...MONO, fontSize: 11, color: STAGE.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {label}
                                    </span>
                                    <div style={{ height: 8, background: 'rgba(255,255,255,0.06)', position: 'relative' }}>
                                        <div
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                transformOrigin: 'left center',
                                                transform: `scaleX(${pct})`,
                                                background: i === 0 ? STAGE.green : STAGE.cyan,
                                            }}
                                        />
                                    </div>
                                    <span style={{ ...MONO, fontSize: 11, color: STAGE.ink, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                        {v}{unit}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        case 'Table': {
            const headers = Array.isArray(args[0]) ? args[0].map(String) : [];
            const rows = Array.isArray(args[1]) ? args[1] : [];
            return (
                <div style={{ overflowX: 'auto', border: `1px solid ${STAGE.rule}` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', ...MONO, fontSize: 11 }}>
                        {headers.length > 0 && (
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${STAGE.rule}`, color: STAGE.muted, textAlign: 'left' }}>
                                    {headers.map((h, i) => (
                                        <th key={i} style={{ padding: '8px 10px', fontWeight: 500 }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                        )}
                        <tbody>
                            {rows.map((r, ri) => {
                                const cells: OpenUIValue[] = isOpenUIComponent(r) && r.type === 'Row'
                                    ? r.args
                                    : Array.isArray(r) ? r : [r];
                                return (
                                    <tr key={ri} style={{ borderBottom: ri < rows.length - 1 ? `1px solid ${STAGE.rule}` : undefined }}>
                                        {cells.map((cell: OpenUIValue, ci: number) => (
                                            <td key={ci} style={{ padding: '8px 10px', color: STAGE.ink }}>
                                                <OpenUIRenderer node={cell} onAction={onAction} compact={compact} />
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            );
        }

        case 'Tag': {
            const text = typeof args[0] === 'string' ? args[0] : String(args[0] ?? '');
            const tone = typeof args[1] === 'string' ? args[1] : 'neutral';
            const color = tone === 'good' || tone === 'ok' ? STAGE.green
                : tone === 'warn' ? STAGE.orange
                : tone === 'alert' ? STAGE.red
                : STAGE.cyan;
            return (
                <span
                    style={{
                        ...MONO,
                        fontSize: 11,
                        padding: '2px 7px',
                        border: `1px solid ${stageAlpha(color, 0.4)}`,
                        background: stageAlpha(color, 0.1),
                        color,
                    }}
                >
                    {text}
                </span>
            );
        }

        case 'Alert': {
            const title = typeof args[0] === 'string' ? args[0] : 'Note';
            const body = typeof args[1] === 'string' ? args[1] : '';
            return (
                <div
                    style={{
                        padding: '10px 12px',
                        borderLeft: `2px solid ${STAGE.yellow}`,
                        background: stageAlpha(STAGE.yellow, 0.06),
                        ...MONO,
                        fontSize: 11,
                        color: STAGE.ink,
                    }}
                >
                    <strong style={{ color: STAGE.yellow }}>{title}: </strong>
                    <span style={{ color: STAGE.muted }}>{body}</span>
                </div>
            );
        }

        case 'FollowUpBlock': {
            const items = Array.isArray(args[0]) ? args[0] : args;
            return (
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 6,
                        paddingTop: 8,
                        borderTop: `1px solid ${STAGE.rule}`,
                    }}
                >
                    {items.map((item, idx) => (
                        <OpenUIRenderer key={idx} node={item} onAction={onAction} compact={compact} />
                    ))}
                </div>
            );
        }

        case 'FollowUpItem': {
            const label = typeof args[0] === 'string' ? args[0] : 'Explore';
            const payload = typeof args[1] === 'string' ? args[1] : label;
            return (
                <button
                    type="button"
                    className="tap-hit"
                    onClick={() => onAction?.(label, payload)}
                    style={{
                        ...MONO,
                        fontSize: 11,
                        padding: '5px 10px',
                        minHeight: 32,
                        cursor: 'pointer',
                        border: `1px solid ${stageAlpha(STAGE.cyan, 0.38)}`,
                        background: stageAlpha(STAGE.cyan, 0.08),
                        color: STAGE.cyan,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                    }}
                >
                    <span>{label}</span>
                    <span aria-hidden="true">→</span>
                </button>
            );
        }

        case 'Button': {
            const label = typeof args[0] === 'string' ? args[0] : 'Action';
            const variant = typeof args[1] === 'string' ? args[1] : 'secondary';
            const payload = typeof args[2] === 'string' ? args[2] : label;
            const isPrimary = variant === 'primary';
            return (
                <button
                    type="button"
                    className="tap-hit"
                    onClick={() => onAction?.(label, payload)}
                    style={{
                        ...MONO,
                        fontSize: 11,
                        padding: '8px 14px',
                        minHeight: 38,
                        cursor: 'pointer',
                        border: `1px solid ${isPrimary ? STAGE.green : STAGE.rule}`,
                        background: isPrimary ? stageAlpha(STAGE.green, 0.14) : 'transparent',
                        color: isPrimary ? STAGE.green : STAGE.ink,
                        textAlign: 'center',
                    }}
                >
                    {label}
                </button>
            );
        }

        default: {
            return (
                <div style={{ ...MONO, fontSize: 11, color: STAGE.muted, padding: 8, border: `1px solid ${STAGE.rule}` }}>
                    {type}({args.length})
                </div>
            );
        }
    }
}

export default OpenUIRenderer;
